import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import RichTextComponent from '@eneco/components/RichText/RichText';
import { NotificationBox, Table as SparkyTable, Text } from '@eneco/sparky';

import type { SimpleTableContent, SimpleTableProperty } from './simple-table-content.types';

/**
 * `SimpleTable` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy Sitecore lane (`libs/sitecore/components/content/src/SimpleTable/SimpleTable.tsx`):
 * a Sparky `Table` with one row per `propertiesList` entry, each row holding two `Text` cells (a label
 * and its value), both treated as rich text like the legacy component's own `<RichText>` wrapping.
 * `SimpleTable` carries no rendering parameters on either provider (the legacy component's `params` type
 * is `{}`), so there is nothing to read via `useSetting`.
 *
 * Both adapters hand each row over as `{ label, text }` (see `simple-table-content.types.ts`). Rows with
 * neither are dropped rather than rendered blank, and if no row has content at all, this falls back to
 * the title-only + dev warning `Table.tsx` uses for its own row gap, instead of an empty table frame.
 */
const SimpleTable: FC = () => {
  const content = useContent<SimpleTableContent>();
  const properties = content.propertiesList?.filter(hasContent) ?? [];

  if (properties.length === 0) {
    return (
      <>
        {content.title && <Text weight="bold">{content.title}</Text>}
        {process.env.NODE_ENV !== 'production' && (
          <NotificationBox
            isAlert={false}
            title="SimpleTable properties not migrated"
            text="propertiesList resolves to unresolved references on this content, not inline label/text pairs."
          />
        )}
      </>
    );
  }

  return (
    <SparkyTable title={content.title}>
      {properties.map((property, index) => (
        <SparkyTable.Row key={index}>
          <PropertyCell value={property.label} />
          <PropertyCell value={property.text} />
        </SparkyTable.Row>
      ))}
    </SparkyTable>
  );
};

const hasContent = (property: SimpleTableProperty): boolean => Boolean(property.label || property.text);

const PropertyCell: FC<{ value: unknown }> = ({ value }) => {
  const { adapter } = useCms();
  const html = adapter.toRichText(value);
  return <Text>{html ? <RichTextComponent html={html} /> : null}</Text>;
};

export default SimpleTable;
